import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import BaseTabs from 'components/atoms/base-tabs';
import useUser from 'utils/useUser';
import { usePatientCallInfo } from 'infrastructure/hooks/patient/use-call-info';
import {
  AgencyServiceDeliveryModel,
  ClinicServiceDeliveryModel,
} from 'infrastructure/enums';
import BaseLoader from 'components/atoms/base-loader';
import LinkedCallReadingsModal from 'components/molecules/linked-call-readings-modal';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';

import CallDashboardPatientInfoTab from './tabs/patient-info';
import s from './styles.module.scss';
import CallDashboardContactAvailabilityTab from './tabs/contact-availability';
import CallDashboardAppointmentTab from './tabs/appointment';
import CallDashboardKeyNotesTab from './tabs/key-notes';
import CallDashboardCallHistoryTab from './tabs/call-history';
import PatientSelection from './patient-selection';
import UnknownNumberForm from './unknown-number-form';
import CallDashboardSaasInfo from '../saas-info';

import type { PatientSelect } from 'infrastructure/classes/select/patient-select';
import type { IBaseTab } from 'components/atoms/base-tabs';

interface ICallDashboardDetailTabsProps {
  patients: PatientSelect[];
  patientGuid: string;
  isUnknownNumber: boolean;
  isRpmCall?: boolean;
  isOnCall: boolean;
  onPatientSwitch?: (patientGuid: string) => void;
}

const CallDashboardDetailTabs: React.FC<ICallDashboardDetailTabsProps> = (
  props,
) => {
  const {
    patients,
    patientGuid: patientGuidProp,
    isUnknownNumber,
    isRpmCall,
    isOnCall,
    onPatientSwitch,
  } = props;

  const [selectContentEl, setSelectContentEl] = useState<HTMLElement | null>(
    null,
  );
  const [patientGuid, setPatientGuidState] = useState(patientGuidProp);

  const setPatientGuid = (guid: string) => {
    setPatientGuidState(guid);
    onPatientSwitch?.(guid);
  };
  const { patientInfo } = usePatientInfo({ patientGuid });
  const { data: callInfo, isFetching } = usePatientCallInfo({ patientGuid });

  const isSaaS =
    callInfo?.clinicServiceDeliveryModel === ClinicServiceDeliveryModel.SaaS ||
    callInfo?.agencyServiceDeliveryModel === AgencyServiceDeliveryModel.SaaS;

  const { t } = useTranslation();

  const { userType } = useUser();

  const items = useMemo(
    () => (guid: string) => {
      const tabs: IBaseTab[] = [
        {
          key: 'patientInfo',
          label: t('labels.patientInfo'),
          children: (
            <CallDashboardPatientInfoTab
              patientGuid={guid}
              isRpmCall={isRpmCall}
              isOnCall={isOnCall}
            />
          ),
        },
        {
          key: 'callHistory',
          label: t('labels.callHistory'),
          children: <CallDashboardCallHistoryTab patientGuid={guid} />,
        },
        {
          key: 'contactAvailability',
          label: t('labels.contactAvailability'),
          children: <CallDashboardContactAvailabilityTab patientGuid={guid} />,
        },
        {
          key: 'appointment',
          label: t('labels.appointment'),
          children: <CallDashboardAppointmentTab patientGuid={guid} />,
        },
        {
          key: 'keyNotes',
          label: t('labels.keyNotes'),
          children: <CallDashboardKeyNotesTab patientGuid={guid} />,
          flat: true,
        },
      ];

      return tabs;
    },
    [patientGuid, userType, isOnCall],
  );

  useEffect(() => {
    setPatientGuidState(patientGuidProp);
    if (patients.length && !patientGuidProp)
      setPatientGuidState(patients[0].guid);
  }, [patients, patientGuidProp]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const element = document.getElementById('contact-display-name');
      setSelectContentEl(element);
    }, 200);
    return () => clearTimeout(timer);
  }, [patients, patientGuidProp]);

  return (
    <>
      <div>
        {(!!patients.length || isUnknownNumber) &&
          selectContentEl &&
          createPortal(
            <PatientSelection
              patients={patients}
              patientGuid={patientGuid}
              isUnknownNumber={isUnknownNumber}
              onChange={setPatientGuid}
            />,
            selectContentEl,
          )}
        {patientGuid ? (
          <BaseLoader loading={isFetching}>
            {isSaaS ? (
              <CallDashboardSaasInfo info={callInfo} />
            ) : (
              <BaseTabs
                key={patientGuid}
                tabs={items(patientGuid)}
                contentClassName={s.content}
              />
            )}
          </BaseLoader>
        ) : (
          <UnknownNumberForm onSubmit={setPatientGuid} />
        )}
      </div>
      {patientInfo?.clinicGuid && (
        <LinkedCallReadingsModal
          patientGuid={patientGuid}
          clinicGuid={patientInfo.clinicGuid}
          isOpenConnect={false}
        />
      )}
    </>
  );
};

export default CallDashboardDetailTabs;
